import React, { useState } from "react";
import { AdminProvider, useAdmin } from "../../context/AdminContext";
import { useSeason } from "../../context/SeasonContext";

// ── Group: Game Management ────────────────────────────────────────────────────
import SchedulePanel from "./panels/SchedulePanel";
import GameResultsPanel from "./panels/GameResultsPanel";
import ScrimmagesPanel from "./panels/ScrimmagesPanel";
import StatAdjustmentPanel from "./panels/StatAdjustmentPanel";
import AllStarRosterPanel from "./panels/AllStarRosterPanel";

// ── Group: Team Management ────────────────────────────────────────────────────
import TeamsPanel from "./panels/TeamsPanel";
import { UserManagementPanel } from "./panels/UserManagementPanel";
import RegistrationsPanel from "./panels/RegistrationsPanel";
import PlayerClaimsPanel from "./panels/PlayerClaimsPanel";

// ── Group: League Management ──────────────────────────────────────────────────
import LeagueSettingsPanel from "./panels/LeagueSettingsPanel";
import { SponsorsPanel } from "./panels/SponsorsPanel";
import AuditLogPanel from "./panels/AuditLogPanel";
import PhotoAlbumsPanel from "./panels/PhotoAlbumsPanel";
import SeasonManagementPanel from "./panels/SeasonManagementPanel";
import WeeklyHadithPanel from "./panels/WeeklyHadithPanel";

import { useSearchParams } from "react-router-dom";

// ─── Icons ────────────────────────────────────────────────────────────────────

const Icons = {
  // Game Management
  GameResults: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  Schedule: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  ),
  StatRequests: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  ),
  Scrimmages: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  AllStar: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  ),
  // Team Management
  Teams: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  Users: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  ),
  Registrations: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  Claims: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  ),
  // League Management
  Seasons: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  Settings: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
  Sponsors: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
  Photos: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  ),
  Audit: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  Hadith: () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  ),
  ChevronDown: () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  ),
  ChevronRight: () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  ),
  Menu: () => (
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  ),
};

// ─── Tab Config ───────────────────────────────────────────────────────────────

const GROUPS = [
  {
    id: "game",
    label: "Game Management",
    tabs: [
      {
        id: "gameresults",
        label: "Game Results",
        Panel: GameResultsPanel,
        Icon: Icons.GameResults,
      },
      {
        id: "schedule",
        label: "Schedule",
        Panel: SchedulePanel,
        Icon: Icons.Schedule,
      },
      {
        id: "adjustments",
        label: "Stat Requests",
        Panel: StatAdjustmentPanel,
        Icon: Icons.StatRequests,
      },
      {
        id: "scrimmages",
        label: "Scrimmages",
        Panel: ScrimmagesPanel,
        Icon: Icons.Scrimmages,
      },
      {
        id: "allstar",
        label: "All-Star Game",
        Panel: AllStarRosterPanel,
        Icon: Icons.AllStar,
      },
    ],
  },
  {
    id: "team",
    label: "Team Management",
    tabs: [
      {
        id: "teams",
        label: "Teams & Captains",
        Panel: TeamsPanel,
        Icon: Icons.Teams,
      },
      {
        id: "users",
        label: "User Management",
        Panel: UserManagementPanel,
        Icon: Icons.Users,
      },
      {
        id: "regs",
        label: "Registrations",
        Panel: RegistrationsPanel,
        Icon: Icons.Registrations,
      },
      {
        id: "claims",
        label: "Player Claims",
        Panel: PlayerClaimsPanel,
        Icon: Icons.Claims,
      },
    ],
  },
  {
    id: "league",
    label: "League Management",
    tabs: [
      {
        id: "seasons",
        label: "Seasons",
        Panel: SeasonManagementPanel,
        Icon: Icons.Seasons,
      },
      {
        id: "settings",
        label: "League Settings",
        Panel: LeagueSettingsPanel,
        Icon: Icons.Settings,
      },
      {
        id: "sponsors",
        label: "Sponsors",
        Panel: SponsorsPanel,
        Icon: Icons.Sponsors,
      },
      {
        id: "photos",
        label: "Photo Albums",
        Panel: PhotoAlbumsPanel,
        Icon: Icons.Photos,
      },
      {
        id: "hadiths",
        label: "Weekly Hadiths",
        Panel: WeeklyHadithPanel,
        Icon: Icons.Hadith,
      },
      {
        id: "audit",
        label: "Audit Log",
        Panel: AuditLogPanel,
        Icon: Icons.Audit,
      },
    ],
  },
];

// Default to Game Management → Game Results
//const DEFAULT_GROUP = "game";
const DEFAULT_TAB = "gameresults";

// Short labels for the switcher dropdown — mirrors SeasonManagementPanel's
// status vocabulary without importing its internals.
const STATUS_LABEL = {
  upcoming: "Upcoming",
  registration: "Registration",
  active: "Active",
  playoffs: "Playoffs",
  completed: "Completed",
};

// Panels whose data is genuinely season-scoped and should follow the switcher.
// Registrations is excluded because it always follows the open registration
// season on its own. Audit Log is excluded because it's global.
const SEASON_SCOPED_TABS = new Set([
  "gameresults",
  "schedule",
  "adjustments",
  "scrimmages",
  "teams",
  "claims",
  "photos",
  "allstar",
  "users", // User Management filters captains by season
  "settings", // League Settings are per-season
  "sponsors", // Sponsors should be season-filtered
  "hadiths", // Weekly Hadiths are per-season
]);

// ─── Season Switcher ───────────────────────────────────────────────────────────

const SeasonSwitcher = ({ compact = false }) => {
  const { activeSeason, seasons, setActiveSeason } = useAdmin();
  const { activeSeason: liveSeason } = useSeason();

  if (!seasons?.length) return null;

  // Live/registration seasons first, then upcoming, then completed (most recent first)
  const priority = {
    playoffs: 0,
    active: 0,
    registration: 1,
    upcoming: 2,
    completed: 3,
  };
  const sorted = [...seasons].sort(
    (a, b) => (priority[a.status] ?? 4) - (priority[b.status] ?? 4),
  );

  const isViewingLive = !liveSeason || activeSeason?.id === liveSeason.id;

  // Status badge colors
  const statusColors = {
    playoffs:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    active:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    registration:
      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    upcoming: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
    completed: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <select
          value={activeSeason?.id || ""}
          onChange={(e) => {
            const season = seasons.find(
              (s) => s.id === parseInt(e.target.value),
            );
            if (season) setActiveSeason(season);
          }}
          className="border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm font-medium
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                     focus:outline-none focus:ring-2 focus:ring-orange-500 shadow-sm"
        >
          {sorted.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {!isViewingLive && (
          <span
            className="text-amber-600 dark:text-amber-400"
            title="Not viewing live season"
          >
            ⚠
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Season
          </span>
        </div>
        <select
          value={activeSeason?.id || ""}
          onChange={(e) => {
            const season = seasons.find(
              (s) => s.id === parseInt(e.target.value),
            );
            if (season) setActiveSeason(season);
          }}
          className="flex-1 border dark:border-gray-600 rounded-lg px-3 py-2 text-sm font-semibold
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                     focus:outline-none focus:ring-2 focus:ring-orange-500 shadow-sm
                     hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
        >
          {sorted.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {activeSeason && (
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[activeSeason.status] || statusColors.completed}`}
          >
            {STATUS_LABEL[activeSeason.status] || activeSeason.status}
          </span>
          {!isViewingLive && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Historical View
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Sidebar Navigation Group Component ───────────────────────────────────────

const SidebarGroup = ({
  group,
  activeTab,
  onTabClick,
  isExpanded,
  onToggle,
}) => {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
      >
        <span className="uppercase tracking-wider text-xs">{group.label}</span>
        <span
          className={`transition-transform duration-200 ${isExpanded ? "rotate-0" : "-rotate-90"}`}
        >
          <Icons.ChevronDown />
        </span>
      </button>

      {isExpanded && (
        <div className="mt-1 space-y-0.5">
          {group.tabs.map((tab) => {
            const Icon = tab.Icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onTabClick(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                  isActive
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50"
                }`}
              >
                <Icon />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Inner component (has access to AdminContext) ─────────────────────────────

const AdminDashboardInner = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  // find which group owns the requested tab
  const findGroupForTab = (tabId) =>
    GROUPS.find((g) => g.tabs.some((t) => t.id === tabId));

  const initialTab =
    tabParam && findGroupForTab(tabParam) ? tabParam : DEFAULT_TAB;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Initially expand the group containing the active tab
    return GROUPS.reduce((acc, group) => {
      acc[group.id] = group.tabs.some((t) => t.id === initialTab);
      return acc;
    }, {});
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Find current tab info
  const currentGroup = findGroupForTab(activeTab);
  const currentTab =
    currentGroup?.tabs.find((t) => t.id === activeTab) ?? GROUPS[0].tabs[0];
  const { Panel } = currentTab;

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    // On mobile, close sidebar after selection
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="flex min-h-screen overflow-x-hidden">
      {/* ── Mobile Overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar Navigation ── */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen lg:h-[calc(100vh-0px)] w-64 flex-shrink-0 bg-white dark:bg-gray-800 border-r dark:border-gray-700 shadow-lg lg:shadow-none z-50 lg:z-0 transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-5 border-b dark:border-gray-700">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Admin Dashboard
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              League Management
            </p>
          </div>

          {/* Season Switcher in Sidebar */}
          {SEASON_SCOPED_TABS.has(currentTab.id) && (
            <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
              <SeasonSwitcher />
            </div>
          )}

          {/* Navigation Groups */}
          <nav className="flex-1 overflow-y-auto p-3">
            {GROUPS.map((group) => (
              <SidebarGroup
                key={group.id}
                group={group}
                activeTab={activeTab}
                onTabClick={handleTabClick}
                isExpanded={expandedGroups[group.id]}
                onToggle={() => toggleGroup(group.id)}
              />
            ))}
          </nav>
        </div>
      </aside>

      {/* ── Main Content Area ── */}
      <div className="flex-1 min-w-0 space-y-6 pb-6 lg:pl-6 overflow-x-hidden">
        {/* Mobile Menu Button & Header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 flex-shrink-0 text-black dark:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Icons.Menu />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                  {currentTab.label}
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {currentGroup?.label}
                </p>
              </div>
            </div>
            {SEASON_SCOPED_TABS.has(currentTab.id) && (
              <div className="flex-shrink-0">
                <SeasonSwitcher compact />
              </div>
            )}
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold mb-1 text-gray-900 dark:text-white">
                {currentTab.label}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {currentGroup?.label}
              </p>
            </div>
          </div>
        </div>

        {/* Panel Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 overflow-x-hidden">
          <Panel />
        </div>
      </div>
    </div>
  );
};

// ─── Public export: wraps everything in AdminProvider ─────────────────────────

const AdminDashboard = () => (
  <AdminProvider>
    <AdminDashboardInner />
  </AdminProvider>
);

export default AdminDashboard;
